import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, FAQSection, CTASection } from '../../components/PageTemplate';

export default function TenantsFAQs() {
  const faqCategories = [
    {
      title: 'Getting Started',
      faqs: [
        {
          question: 'Is LetWise free for tenants?',
          answer: 'Yes! Creating a profile, searching for properties, and applying is completely free. You only pay your rent and deposit as you would normally.'
        },
        {
          question: 'What do I need to create a profile?',
          answer: 'Just your email address to start. To apply for properties, you\'ll need ID (passport or driving license), proof of income, and your current address history.'
        },
        {
          question: 'Can I apply to multiple properties?',
          answer: 'Yes. Your profile is reusable, so you can apply to as many properties as you like with one click each.'
        }
      ]
    },
    {
      title: 'Applications & Referencing',
      faqs: [
        {
          question: 'How long does an application take?',
          answer: 'Applications are submitted instantly. Referencing typically takes 24-72 hours depending on how quickly your employer and previous landlord respond.'
        },
        {
          question: 'Will applying affect my credit score?',
          answer: 'No. We perform a "soft" credit check which doesn\'t appear on your credit file and doesn\'t affect your score.'
        },
        {
          question: 'What if I\'m a first-time renter?',
          answer: 'No problem. You can use alternative references (university accommodation, parental home) or provide a guarantor.'
        },
        {
          question: 'What if I\'m self-employed?',
          answer: 'Instead of payslips, you\'ll provide your last 2-3 years of accounts or tax returns. We may also request an accountant reference.'
        }
      ]
    },
    {
      title: 'Deposits & Payments',
      faqs: [
        {
          question: 'How is my deposit protected?',
          answer: 'Your deposit is registered with a government-approved scheme (TDS, DPS, or mydeposits). You\'ll receive confirmation directly from them.'
        },
        {
          question: 'How do I pay my rent?',
          answer: 'Rent is collected by Direct Debit on an agreed date each month. It\'s automatic, so you don\'t need to remember to pay.'
        },
        {
          question: 'What if I can\'t pay my rent on time?',
          answer: 'Contact us before the due date if you\'re having difficulties. We can discuss options. Ignoring it will result in arrears letters and potential action.'
        }
      ]
    },
    {
      title: 'During Your Tenancy',
      faqs: [
        {
          question: 'How do I report a repair?',
          answer: 'Open the app, tap "Report Issue", describe the problem, add photos, and submit. You\'ll get updates as the repair progresses.'
        },
        {
          question: 'How do inspections work?',
          answer: 'You\'ll be notified when an inspection is due. Where possible, you can complete it yourself via the app. Some may require an in-person visit.'
        },
        {
          question: 'Can I make changes to the property?',
          answer: 'You need landlord permission for any changes (painting, fixtures, etc.). Request through the app and wait for approval.'
        },
        {
          question: 'What counts as an emergency?',
          answer: 'No heating in winter, no hot water, water leaks, gas leaks, security issues (broken locks/windows). These are prioritized and handled urgently.'
        }
      ]
    },
    {
      title: 'Moving Out',
      faqs: [
        {
          question: 'How much notice do I need to give?',
          answer: 'This depends on your tenancy type. Check your portal for the exact period - it\'s usually 1-2 months.'
        },
        {
          question: 'How do I get my deposit back?',
          answer: 'After the check-out inspection, we compare the property to your check-in. If there\'s no damage beyond fair wear, your deposit is returned within 10 days.'
        },
        {
          question: 'What if I disagree with deductions?',
          answer: 'You can dispute deductions. If we can\'t agree, the deposit scheme adjudicates based on evidence. Your check-in photos are crucial here.'
        }
      ]
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="FAQs"
        title="Tenant FAQs"
        description="Find answers to common questions about renting through LetWise."
        gradient="from-slate-100 via-white to-slate-50"
      />

      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {faqCategories.map((category, catIndex) => (
            <div key={catIndex} className="mb-12 last:mb-0">
              <h2 className="text-xl sm:text-2xl font-bold mb-6 pb-2 border-b">
                {category.title}
              </h2>
              <div className="space-y-4">
                {category.faqs.map((faq, faqIndex) => (
                  <FAQItem key={faqIndex} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTASection
        title="Still have questions?"
        description="Our support team is here to help. Get in touch anytime."
        primaryCTA={{ href: '/contact', label: 'Contact Us' }}
        secondaryCTA={{ href: '/register', label: 'Create Profile' }}
      />
    </PublicLayout>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 sm:px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors"
      >
        <span className="font-medium text-sm sm:text-base">{question}</span>
        <span className={`flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center transition-transform ${open ? 'rotate-180' : ''}`}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${open ? 'max-h-96' : 'max-h-0'}`}>
        <div className="px-4 sm:px-6 pb-4 text-sm sm:text-base text-slate-600">
          {answer}
        </div>
      </div>
    </div>
  );
}
